// Offline work queue for Vessels & Quotes (staff use).
// - cachePut/cacheGet: last-known GET data so pages render offline.
// - enqueue/saveFile: queued creates/edits and image files (IndexedDB).
// - flush: replays the queue in order when back online, uploading queued
//   files first and mapping temp ids -> real ids. Last write wins.
const DB_NAME = "sos-offline";

function idb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "id", autoIncrement: true });
      if (!db.objectStoreNames.contains("files")) db.createObjectStore("files");
      if (!db.objectStoreNames.contains("cache")) db.createObjectStore("cache");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, store, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const out = fn(s);
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
  });
}

const listeners = new Set();
export const onQueueChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const notify = async () => {
  const n = await pendingCount();
  listeners.forEach((fn) => fn(n));
};

export const isNetworkError = (e) => !e?.response;

export async function cachePut(key, data) {
  try {
    const db = await idb();
    await tx(db, "cache", "readwrite", (s) => s.put({ data, at: Date.now() }, key));
  } catch {}
}

export async function cacheGet(key) {
  try {
    const db = await idb();
    const rec = await new Promise((res, rej) => {
      const r = db.transaction("cache").objectStore("cache").get(key);
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
    return rec ? rec.data : null;
  } catch {
    return null;
  }
}

export async function enqueue(action) {
  const db = await idb();
  await tx(db, "queue", "readwrite", (s) => s.add({ ...action, queued_at: Date.now() }));
  notify();
}

export async function saveOfflineFile(file) {
  const key = `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const db = await idb();
  await tx(db, "files", "readwrite", (s) => s.put(file, key));
  return `__offline__:${key}`;
}

export async function pendingCount() {
  try {
    const db = await idb();
    return await new Promise((res, rej) => {
      const r = db.transaction("queue").objectStore("queue").count();
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  } catch {
    return 0;
  }
}

async function allActions() {
  const db = await idb();
  return new Promise((res, rej) => {
    const r = db.transaction("queue").objectStore("queue").getAll();
    r.onsuccess = () => res(r.result || []);
    r.onerror = () => rej(r.error);
  });
}

async function removeAction(id) {
  const db = await idb();
  await tx(db, "queue", "readwrite", (s) => s.delete(id));
}

async function getFile(key) {
  const db = await idb();
  return new Promise((res, rej) => {
    const r = db.transaction("files").objectStore("files").get(key);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

async function removeFile(key) {
  const db = await idb();
  await tx(db, "files", "readwrite", (s) => s.delete(key));
}

// Deep-walk a payload, uploading any "__offline__:key" strings and
// substituting the real storage path.
async function resolvePlaceholders(api, value, uploaded) {
  if (typeof value === "string" && value.startsWith("__offline__:")) {
    const key = value.slice("__offline__:".length);
    if (!uploaded[key]) {
      const file = await getFile(key);
      if (!file) return "";
      const fd = new FormData();
      fd.append("file", file, file.name || "offline-upload.jpg");
      const { data } = await api.post("/upload", fd);
      uploaded[key] = data.path;
      await removeFile(key);
    }
    return uploaded[key];
  }
  if (Array.isArray(value)) {
    const out = [];
    for (const v of value) out.push(await resolvePlaceholders(api, v, uploaded));
    return out;
  }
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = await resolvePlaceholders(api, v, uploaded);
    return out;
  }
  return value;
}

const mapId = (id, mapping) => (id && mapping[id] ? mapping[id] : id);

export async function flush(api) {
  const actions = (await allActions()).sort((a, b) => a.id - b.id);
  if (actions.length === 0) return { synced: 0, failed: 0 };
  const mapping = {};
  const uploaded = {};
  let synced = 0;
  let failed = 0;
  for (const a of actions) {
    try {
      const payload = await resolvePlaceholders(api, a.payload || {}, uploaded);
      if (a.type === "create_vessel") {
        const { data } = await api.post("/vessels", payload);
        if (a.tempId) mapping[a.tempId] = data.id;
      } else if (a.type === "update_vessel") {
        await api.put(`/vessels/${mapId(a.targetId, mapping)}`, payload);
      } else if (a.type === "vessel_media") {
        await api.put(`/vessels/${mapId(a.targetId, mapping)}/media`, payload);
      } else if (a.type === "create_quote") {
        const { data } = await api.post("/quotes", payload);
        if (a.tempId) mapping[a.tempId] = data.id;
      } else if (a.type === "update_quote") {
        await api.put(`/quotes/${mapId(a.targetId, mapping)}`, payload);
      }
      await removeAction(a.id);
      synced += 1;
    } catch (e) {
      if (isNetworkError(e)) {
        // Still offline — stop and keep the rest queued.
        notify();
        return { synced, failed, offline: true };
      }
      // Server rejected it — drop so the queue doesn't jam.
      await removeAction(a.id);
      failed += 1;
    }
  }
  notify();
  return { synced, failed };
}
